import CPromise from '../cPromise';
import { isPromise } from '../isPromise';
import Bus from './bus';
import type { BusSubscribeOptions } from './bus_subscribe';
import type { StandardSchemaV1 } from '@standard-schema/spec';

export interface BusOnceOptions extends BusSubscribeOptions {
    /** Timeout in ms. Set to `false` to disable. */
    timeout?: number | false;
}

export function bus_once<T = unknown>(this: Bus, topic: string, options?: BusOnceOptions): CPromise<T>;

export function bus_once<T = unknown>(
    this: Bus,
    topic: string,
    schema: StandardSchemaV1<unknown, T>,
    options?: BusOnceOptions,
): CPromise<T>;

export function bus_once<T = unknown>(
    this: Bus,
    topic: string,
    ...args: [BusOnceOptions?] | [StandardSchemaV1<unknown, T>, BusOnceOptions?]
): CPromise<T> {
    let schema: StandardSchemaV1<unknown, T> | undefined;
    let options: BusOnceOptions | undefined;

    if (args[0] && '~standard' in args[0]) {
        [schema, options] = args as [StandardSchemaV1<unknown, T>, BusOnceOptions?];
    } else {
        [options] = args as [BusOnceOptions?];
    }

    const { correlationGuid, debug = this.options.debug, signal, measureTime } = options ?? {};
    let { timeout = this.options.timeout } = options ?? {};

    if (typeof timeout === 'number') {
        timeout = Math.max(timeout, 0);
    }

    const promise = new CPromise<T>((resolve, reject, onCancel) => {
        const cancelSubscription = this.subscribe<T>(
            topic,
            async (data, _topic, pkg) => {
                cancel();

                if (pkg?.error) {
                    reject(pkg.error);
                } else {
                    if (schema) {
                        let result = schema['~standard'].validate(data);

                        if (isPromise(result)) {
                            result = await result;
                        }

                        if (result.issues) {
                            reject(
                                Object.assign(new Error(`Validation failed for topic ${topic}`), {
                                    data,
                                    issues: result.issues,
                                    get baseModel() {
                                        return pkg;
                                    },
                                }),
                            );
                            return;
                        }

                        data = result.value;
                    }

                    resolve(data);
                }
            },
            { correlationGuid, debug, measureTime },
        );

        let timeoutId: ReturnType<typeof setTimeout> | undefined;
        if (typeof timeout === 'number') {
            timeoutId = setTimeout(() => {
                cancel();
                reject(Object.assign(new Error(`${topic} timed out after ${timeout}ms`), { isTimeout: true }));
            }, timeout);
        }

        const cancel = () => {
            cancelSubscription();
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };

        onCancel(cancel);
    });

    signal?.addEventListener('abort', () => promise.cancel());

    return promise;
}
